import { StandardEvents } from '@shopify/events';

const OPTION_NAMES = ['Modelagem e camiseta', 'Cor do número', 'Tamanho'];
const MODELS = ['Masculina', 'Feminina'];
const SHIRTS = ['branca', 'preta'];
const INKS = ['Azul-claro', 'Bordô', 'Alaranjado', 'Rosa', 'Contraste', 'Marrom', 'Cinza'];
const SIZES = ['P', 'M', 'G', 'GG'];
const nativeId = (value) => {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
  return /^[1-9]\d{0,19}$/.test(String(value || '')) ? String(value) : null;
};

export function logicalNumbersChoice(variant) {
  if (!Array.isArray(variant?.options) || variant.options.length !== 3 || variant.options.some((value) => typeof value !== 'string')) return null;
  const combined = variant?.options?.[0]?.match(/^(Masculina|Feminina) (branca|preta)$/);
  if (!combined || !INKS.includes(variant.options[1]) || !SIZES.includes(variant.options[2])) return null;
  return { model: combined[1], shirt: combined[2], ink: variant.options[1], size: variant.options[2] };
}

export function validateNumbersProduct(data) {
  if (!data || !nativeId(data.productId) || JSON.stringify(data.optionNames) !== JSON.stringify(OPTION_NAMES) || !Array.isArray(data.variants) || data.variants.length !== 112) return false;
  const ids = new Set(), combinations = new Set();
  for (const variant of data.variants) {
    if (!variant || typeof variant !== 'object') return false;
    const id = nativeId(variant.id), logical = logicalNumbersChoice(variant);
    if (!id || !logical || typeof variant.available !== 'boolean' || ids.has(id)) return false;
    const key = JSON.stringify(variant.options);
    if (combinations.has(key)) return false;
    ids.add(id);
    combinations.add(key);
  }
  return ids.has(String(data.selectedVariantId)) && MODELS.every((model) => SHIRTS.every((shirt) => INKS.every((ink) => SIZES.every((size) => combinations.has(JSON.stringify([`${model} ${shirt}`, ink, size]))))));
}

export function resolveNumbersVariant(data, wanted) {
  if (!Array.isArray(data?.variants) || !wanted || !MODELS.includes(wanted.model) || !SHIRTS.includes(wanted.shirt) || !INKS.includes(wanted.ink) || !SIZES.includes(wanted.size)) return null;
  const options = [`${wanted.model} ${wanted.shirt}`, wanted.ink, wanted.size];
  const matches = data.variants.filter((variant) => Array.isArray(variant?.options) && variant.options.length === 3 && variant.options.every((value, index) => value === options[index]));
  return matches.length === 1 ? matches[0] : null;
}

export function numbersMediaUrl(variant, origin) {
  if (!nativeId(variant?.media?.id) || !variant.media.url) return null;
  try {
    const url = new URL(variant.media.url, origin);
    if (url.protocol !== 'https:' || !(url.origin === origin && url.pathname.startsWith('/cdn/shop/') || /(^|\.)cdn\.shopify\.com$/.test(url.hostname))) return null;
    url.searchParams.set('width', '120');
    return url.href;
  } catch (_) { return null; }
}

export function resolveNumbersInitialVariant(data, selected, currentUrl, userChoice = null) {
  if (!selected || !logicalNumbersChoice(selected)) return selected || null;
  if (userChoice) return resolveNumbersVariant(data, userChoice) || selected;
  let url;
  try { url = new URL(currentUrl); } catch (_) { return selected; }
  if (url.searchParams.has('variant') || url.searchParams.has('option_values') || !INKS.includes(data?.defaultInk)) return selected;
  const wanted = { ...logicalNumbersChoice(selected), ink: data.defaultInk };
  const target = resolveNumbersVariant(data, wanted);
  return target && numbersMediaUrl(target, url.origin) ? target : selected;
}

class OrsoNumbersOptions extends HTMLElement {
  async connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.initialUserChoice = null;
    this.addEventListener('change', (event) => {
      if (!event.target.closest?.('[data-numbers-choice]')) return;
      this.initialUserChoice = Object.fromEntries([...this.querySelectorAll('[data-numbers-choice]:checked')].map(input => [input.dataset.numbersChoice, input.value]));
    }, { capture: true, signal: this.controller.signal });
    const token = this.token = Symbol('numbers');
    this.scope = this.closest('.shopify-section');
    this.status = this.querySelector('[data-numbers-status]');
    try { this.product = JSON.parse(this.querySelector('[data-numbers-data]')?.textContent || 'null'); } catch (_) { this.product = null; }
    if (!validateNumbersProduct(this.product) || !this.scope) {
      this.message('As opções completas ainda estão sendo preparadas. Confira as combinações abaixo.');
      return;
    }
    await customElements.whenDefined('variant-picker');
    if (!this.isConnected || this.token !== token) return;
    const picker = this.querySelector('variant-picker');
    if (!picker || typeof picker.updateSelectedOption !== 'function') return;
    this.settled = this.product.variants.find((variant) => variant.id === String(this.product.selectedVariantId));
    this.wanted = logicalNumbersChoice(this.settled);
    this.failure = false;
    this.busy = false;
    this.purchaseObserver = new MutationObserver(() => this.lockPurchaseControls());
    this.purchaseObserver.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
    this.addEventListener(StandardEvents.productSelect, (event) => {
      if (event.target?.closest('variant-picker') !== this.querySelector('variant-picker') || !event.promise) return;
      this.selectionPromise = event.promise;
    }, { signal: this.controller.signal });
    this.addEventListener('change', (event) => {
      const input = event.target.closest?.('[data-numbers-choice]');
      if (!input) return;
      const wanted = { ...this.wanted, [input.dataset.numbersChoice]: input.value };
      if (!resolveNumbersVariant(this.product, wanted)) return;
      this.wanted = wanted;
      this.failure = false;
      this.drawWanted();
      this.busy = true;
      this.lockPurchaseControls();
      this.drain();
    }, { signal: this.controller.signal });
    window.addEventListener('popstate', () => {
      const id = new URL(window.location.href).searchParams.get('variant');
      const variant = this.product.variants.find((value) => value.id === id);
      if (!variant) return;
      this.wanted = logicalNumbersChoice(variant);
      this.failure = false;
      this.drawWanted();
      this.busy = true;
      this.lockPurchaseControls();
      this.drain();
    }, { signal: this.controller.signal });
    this.classList.add('is-enhanced');
    this.concealNative();
    const initial = resolveNumbersInitialVariant(this.product, this.settled, window.location.href, this.initialUserChoice);
    if (initial && initial.id !== this.settled.id) {
      this.wanted = logicalNumbersChoice(initial);
      this.busy = true;
      this.drawWanted();
      this.lockPurchaseControls();
      this.drain();
    } else {
      this.drawWanted();
      this.restorePurchaseControls();
    }
  }

  disconnectedCallback() {
    this.token = null;
    this.controller?.abort();
    this.controller = null;
    this.purchaseObserver?.disconnect();
  }

  message(value) { if (this.status) this.status.textContent = value; }

  concealNative() {
    const native = this.querySelector('[data-numbers-native]');
    native?.setAttribute('aria-hidden', 'true');
    native?.querySelectorAll('input,select,button,a').forEach((element) => { element.tabIndex = -1; });
  }

  drawWanted() {
    this.querySelectorAll('[data-numbers-choice]').forEach((input) => { input.checked = input.value === this.wanted[input.dataset.numbersChoice]; });
    const desired = resolveNumbersVariant(this.product, this.wanted);
    const selectedMedia = numbersMediaUrl(desired, window.location.origin);
    this.toggleAttribute('data-numbers-media-missing', !selectedMedia);
    this.scope.querySelectorAll('add-to-cart-component').forEach((component) => {
      if (selectedMedia) component.setAttribute('data-product-variant-media', selectedMedia);
      else component.removeAttribute('data-product-variant-media');
    });
    this.querySelectorAll('[data-numbers-choice="shirt"]').forEach((input) => {
      const label = input.closest('label'), text = label.querySelector('[data-numbers-shirt-name]');
      const variant = resolveNumbersVariant(this.product, { ...this.wanted, shirt: input.value });
      const url = numbersMediaUrl(variant, window.location.origin);
      let image = label.querySelector('[data-numbers-shirt-photo]');
      if (url) {
        if (!image) {
          image = document.createElement('img');
          image.width = 44; image.height = 44; image.alt = ''; image.loading = 'lazy'; image.decoding = 'async';
          image.setAttribute('data-numbers-shirt-photo', ''); image.setAttribute('aria-hidden', 'true');
          label.append(image);
        }
        image.src = url;
        text?.classList.add('visually-hidden');
      } else {
        image?.remove();
        text?.classList.remove('visually-hidden');
      }
      label.classList.toggle('orso-numbers-choice--photo', !!url);
    });
  }

  purchaseButtons() {
    return [...this.scope.querySelectorAll('button[name="add"],input[name="add"],.sticky-add-to-cart__button')];
  }

  lockPurchaseControls() {
    if (!this.busy && !this.failure && this.settled?.available && numbersMediaUrl(this.settled, window.location.origin)) return;
    for (const button of this.purchaseButtons()) if (!button.disabled) button.disabled = true;
  }

  restorePurchaseControls() {
    const ready = !this.busy && !this.failure && this.settled?.available && !!numbersMediaUrl(this.settled, window.location.origin);
    for (const button of this.purchaseButtons()) button.disabled = !ready;
    this.removeAttribute('aria-busy');
    if (!numbersMediaUrl(this.settled, window.location.origin)) this.message('Foto desta combinação em preparação. Escolha uma opção com imagem disponível.');
    else if (!this.settled?.available) this.message('Esta combinação está indisponível no momento.');
    else this.message('');
  }

  nativeSelection(target) {
    const picker = this.querySelector('variant-picker');
    if (!picker || typeof picker.updateSelectedOption !== 'function') throw new Error('NATIVE_PICKER_UNAVAILABLE');
    const fieldsets = [...picker.querySelectorAll('fieldset')];
    if (fieldsets.length !== 3) throw new Error('NATIVE_OPTIONS_MISMATCH');
    let selected;
    target.options.forEach((value, index) => {
      const input = [...fieldsets[index].querySelectorAll('input[type="radio"]')].find((input) => input.value === value && input.dataset.optionName === OPTION_NAMES[index]);
      if (!input) throw new Error('NATIVE_OPTION_VALUE_MISSING');
      picker.updateSelectedOption(input);
      selected = input;
    });
    selected.dataset.variantId = target.id;
    this.selectionPromise = null;
    selected.dispatchEvent(new Event('change', { bubbles: true }));
    if (!this.selectionPromise?.then) throw new Error('NATIVE_EVENT_NOT_CONFIRMED');
    return this.selectionPromise;
  }

  async drain() {
    if (this.running) return;
    this.running = true;
    const token = this.token;
    try {
      this.setAttribute('aria-busy', 'true');
      this.message('Atualizando a camiseta escolhida.');
      while (this.isConnected && this.token === token) {
        const target = resolveNumbersVariant(this.product, this.wanted);
        if (!target) throw new Error('NO_EXACT_VARIANT');
        if (target.id === this.settled.id && !this.failure) break;
        const promise = this.nativeSelection(target);
        let timer;
        const result = await Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('SELECTION_TIMEOUT')), 20000); })]).finally(() => clearTimeout(timer));
        if (!this.isConnected || this.token !== token) return;
        const resource = result?.detail?.resource;
        if (nativeId(resource?.id) !== target.id) throw new Error('SERVER_VARIANT_MISMATCH');
        await new Promise(requestAnimationFrame);
        const form = this.scope.querySelector('product-form-component form[action]');
        if (String(form?.querySelector('[name="id"]')?.value) !== target.id) throw new Error('FORM_VARIANT_NOT_CONFIRMED');
        this.settled = { ...target, available: resource.available !== false };
        this.concealNative();
      }
      this.busy = false;
      this.drawWanted();
      this.restorePurchaseControls();
    } catch (_) {
      this.busy = false;
      this.failure = true;
      this.lockPurchaseControls();
      this.removeAttribute('aria-busy');
      this.message('Não foi possível confirmar esta combinação. Escolha novamente antes de adicionar ao carrinho.');
    } finally {
      this.running = false;
    }
  }
}

if (!customElements.get('orso-numbers-options')) customElements.define('orso-numbers-options', OrsoNumbersOptions);
